import React, { useEffect, useState } from "react";
import { Plus, Trash2, KeyRound, Copy } from "lucide-react";
import { INPUT, BTN_PRIMARY, BTN_OUTLINE, Field } from "components/admin/AdminUI";
import api from "lib/api";
import { useAuth } from "contexts/AuthContext";

const VALIDITIES = [30, 60, 180, 360, 720];

export default function Tokens() {
  const { user } = useAuth();
  const [tokens, setTokens] = useState([]);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", days: 30, access: "read" });
  const [createdRaw, setCreatedRaw] = useState(null); // token cru, exibido 1x

  const load = () =>
    api.get("/profile/tokens")
      .then(({ data }) => setTokens(data || []))
      .catch(() => setError("Falha ao carregar tokens."));

  useEffect(() => { load(); }, []);

  async function create(e) {
    e.preventDefault();
    setError("");
    try {
      const { data } = await api.post("/profile/tokens", {
        name: form.name, days: Number(form.days), access: form.access,
      });
      setCreatedRaw(data);
      setCreating(false);
      setForm({ name: "", days: 30, access: "read" });
      load();
    } catch (err) {
      const d = err.response?.data;
      setError(typeof d === "object" ? Object.values(d).flat().join(" ") : "Erro ao criar token.");
    }
  }

  async function revoke(t) {
    if (!window.confirm(`Revogar o token "${t.name}"? Esta ação não pode ser desfeita.`)) return;
    try { await api.post(`/profile/tokens/${t.id}/revoke`); load(); }
    catch { setError("Erro ao revogar."); }
  }

  const copy = (v) => navigator.clipboard?.writeText(v);

  return (
    <div className="w-full space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Meus Personal Access Tokens</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Use um PAT para autenticar na API: <span className="font-mono">POST /api/auth/token</span> com
            <span className="font-mono"> client_id</span> e <span className="font-mono">token</span> → recebe um JWE.
          </p>
        </div>
        {!creating && (
          <button className={BTN_PRIMARY} onClick={() => setCreating(true)}>
            <Plus className="mr-1 inline h-4 w-4" /> Novo token
          </button>
        )}
      </div>

      {user?.user_id && (
        <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm">
          <span className="text-muted-foreground">client_id (seu user_id):</span>
          <span className="font-mono">{user.user_id}</span>
          <button className="text-muted-foreground hover:text-foreground" onClick={() => copy(user.user_id)} title="Copiar">
            <Copy className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {error && <p className="text-sm text-red-500">{error}</p>}

      {createdRaw && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4">
          <div className="mb-1 flex items-center gap-2 text-sm font-semibold"><KeyRound className="h-4 w-4" /> Token criado</div>
          <p className="mb-2 text-xs text-muted-foreground">Copie agora — o valor não será exibido novamente.</p>
          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded bg-background px-2 py-1 font-mono text-xs">{createdRaw.token}</code>
            <button className={BTN_OUTLINE} onClick={() => copy(createdRaw.token)}><Copy className="h-4 w-4" /></button>
          </div>
          <button className="mt-3 text-sm text-muted-foreground hover:text-foreground" onClick={() => setCreatedRaw(null)}>Fechar</button>
        </div>
      )}

      {creating && (
        <form onSubmit={create} className="rounded-lg border border-border bg-card p-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Nome">
              <input className={INPUT} value={form.name} autoFocus
                onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="ex.: CI pipeline" />
            </Field>
            <Field label="Validade">
              <select className={INPUT} value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })}>
                {VALIDITIES.map((d) => <option key={d} value={d}>{d} dias</option>)}
              </select>
            </Field>
            <Field label="Acesso">
              <select className={INPUT} value={form.access} onChange={(e) => setForm({ ...form, access: e.target.value })}>
                <option value="read">Somente leitura</option>
                <option value="write">Leitura e escrita</option>
              </select>
            </Field>
          </div>
          <div className="mt-4 flex justify-end gap-3">
            <button type="button" className={BTN_OUTLINE} onClick={() => setCreating(false)}>Cancelar</button>
            <button type="submit" className={BTN_PRIMARY}>Criar</button>
          </div>
        </form>
      )}

      <div className="rounded-lg border border-border bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="px-4 py-2 font-medium">Nome</th>
              <th className="px-4 py-2 font-medium">Prefixo</th>
              <th className="px-4 py-2 font-medium">Acesso</th>
              <th className="px-4 py-2 font-medium">Expira</th>
              <th className="px-4 py-2 font-medium">Último uso</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {tokens.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">Nenhum token.</td></tr>
            )}
            {tokens.map((t) => (
              <tr key={t.id} className="border-b border-border/60 last:border-0">
                <td className="px-4 py-2">{t.name}</td>
                <td className="px-4 py-2 font-mono text-xs">{t.token_prefix}…</td>
                <td className="px-4 py-2">{t.access === "write" ? "leitura/escrita" : "leitura"}</td>
                <td className={"px-4 py-2 " + (t.is_expired ? "text-red-500" : "text-muted-foreground")}>
                  {(t.expires_at || "").slice(0, 10)}{t.is_expired ? " (expirado)" : ""}
                </td>
                <td className="px-4 py-2 text-muted-foreground">{(t.last_used_at || "").slice(0, 10) || "—"}</td>
                <td className="px-4 py-2 text-right">
                  <button className="text-red-500 hover:text-red-400" onClick={() => revoke(t)} title="Revogar">
                    <Trash2 className="inline h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
